import { useState, useEffect } from 'react'
import supabase from '../db/supabase.client.js'

/**
 * Custom hook to manage Supabase authentication session.
 * Handles session state, loading state, errors, and sign out functionality.
 *
 * @returns {Object} Session management object
 * @returns {Object|null} session - Current user session or null
 * @returns {boolean} isLoading - Loading state during session initialization
 * @returns {Error|null} error - Error object if session operation fails
 * @returns {Function} signOut - Function to sign out the current user
 */
export default function useAuthSession() {
  const [session, setSession] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Get initial session on mount
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          setError(error)
        } else {
          setSession(session)
        }
        setIsLoading(false)
      })
      .catch((err) => {
        setError(err)
        setIsLoading(false)
      })

    // Subscribe to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setError(null)
    })

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  /**
   * Sign out the current user.
   * Clears the session and handles errors appropriately.
   */
  const signOut = async () => {
    try {
      setError(null)
      const { error } = await supabase.auth.signOut()
      if (error) {
        setError(error)
      }
    } catch (err) {
      setError(err)
    }
  }

  return { session, isLoading, error, signOut }
}
